import React, { FC, useCallback, useState, useEffect } from "react";

import { Button, EmailInput, LoadingSpinner, PageAlert, validateEmail } from "@xapp/components";

import { IntegrationList } from "components/Integrations";

import { OnboardingProps } from "../models";

import classes from "./style.scss";

export interface IntegrateStepProps {
    /**
     * If network activity is happening
     */
    loading?: boolean;
    /**
     * Friendly message to display to the user.
     */
    loadingMessage?: string;
    /**
     * Error message to display to the user if something goes wrong.
     */
    error?: { error: string, message: string };
    /**
     * Disable all integrations & interactions (except "next" & "previous")
     */
    disabled?: boolean;
    /**
     * The app created in the onboarding that we are integrating to
     */
    app: OnboardingProps;
    /**
     * Optional style applied to the root div
     */
    style?: React.CSSProperties;
    /**
     * Optional class name to apply to the root div
     */
    className?: string;
    /**
     * When provided, it will imit the integrations to those in the
     */
    limitIntegrations?: string[];
    /**
     * Are integrations supported with the current tier
     */
    integrationsSupported?: boolean;
    /**
     * Upgrades the current plan to Leads
     */
    onUpgrade(): void;
    /**
     * User is ready to save everything!
     */
    onNext(app: Pick<OnboardingProps, "leadsContact" | "beta">): void;
    /**
     * User wants to go back to the previous step
     */
    onBack?(): void;
}

export const IntegrateStep: FC<IntegrateStepProps> = props => {

    const {
        app,
        style,
        disabled,
        onBack,
        onNext,
        onUpgrade,
        error,
        className,
        loading,
        loadingMessage,
        limitIntegrations,
        integrationsSupported
    } = props;

    const [leadsContact, setLeadsContact] = useState<string>(app?.leadsContact || "");
    const [leadsContactValid, setLeadsContactValid] = useState<boolean>(validateEmail(app?.leadsContact || ""));
    const [selectedIntegration, setSelectedIntegration] = useState<string[]>([]);

    useEffect(() => {
        if (app?.leadsContact) {
            setLeadsContact(app.leadsContact);
        }
    }, [app]);

    const handleLeadsContactUpdated = useCallback((value: string, valid: boolean) => {
        setLeadsContact(value);
        setLeadsContactValid(valid);
    }, []);

    const handleNextClicked = useCallback(() => {
        onNext({
            leadsContact,
            beta: {
                ...app?.beta,
                onboarding: {
                    ...app?.beta?.onboarding,
                    leadsIntegration: selectedIntegration
                }
            }
        })
    }, [onNext, leadsContact, selectedIntegration, app?.beta]);

    let canProceed = true;

    if (!integrationsSupported) {
        canProceed = leadsContact && leadsContactValid;
    } else {
        // they have selected an integration OR they have
        canProceed = selectedIntegration.length > 0 || (leadsContact && leadsContactValid);
    }

    console.log('limitIntegrations', limitIntegrations);

    return (
        <div style={{ ...style }} className={className}>
            {error ? (
                <PageAlert background="orange" >
                    <p>{error.message}</p>
                    <pre style={{ overflow: "scroll" }}>{error.error}</pre>
                    {/**We need a call to action for them to get help */}
                </PageAlert>
            ) : (<></>)}
            {loading ? (
                <div style={{ padding: "20px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    <LoadingSpinner
                        textStyle={{ fontSize: "1em" }}
                        loadingText={loadingMessage}
                    />
                </div>
            ) : (
                <>
                    <h2>Where do we send your leads?</h2>

                    <p>Bookings, leads and contact requests can be sent where you want them.  </p>

                    <div>
                        <h4>Field Service Management (FSM)</h4>
                        {!integrationsSupported ? (
                            <div className={classes.upgradeWrapper}>
                                <b><i>All Integrations Available starting at Leads+</i></b>
                                <div style={{ fontSize: "0.8em" }}>
                                    <Button variation="primary" label={"Switch plan to Leads+"} onClick={onUpgrade} />
                                </div>
                            </div>
                        ) : <></>}
                        <p className={!integrationsSupported ? classes.disabledText : undefined}>We can integrate directly into your existing workflow by sending the booking or lead to your existing FSM.</p>

                        <i className={!integrationsSupported ? classes.disabledText : undefined}>Select an FSM below:</i>
                        <div style={{ padding: "20px" }}>
                            <IntegrationList
                                selected={selectedIntegration}
                                onSelected={setSelectedIntegration}
                                onboardingMode={true}
                                omitIntegrations={["zapier", "surefire", "lacrm"]}
                                app={app}
                                disabled={disabled || !integrationsSupported}
                                limitIntegrations={limitIntegrations}
                            />
                        </div>
                    </div>

                    <div>
                        <h4>Email</h4>
                        {integrationsSupported ? (
                            <p>Additionally we will send the contact information to an email.  If you want to send to many people, we recommend using an email alias.</p>
                        ) : (
                            <p>On Leads package, we can send all booking, leads, and contact requests to your email.   If you want to send to many people, we recommend using an email alias.</p>
                        )}
                        <EmailInput
                            variation="outlined"
                            label={"Leads Email"}
                            value={leadsContact}
                            onChange={handleLeadsContactUpdated}
                        />
                    </div>

                    <div style={{ padding: "20px", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: typeof onBack === "function" ? "space-between" : "right" }}>
                        {typeof onBack === "function" && (
                            <Button
                                leftIcon="arrow_back"
                                label={`Back`}
                                variation="secondary"
                                onClick={onBack}
                            />
                        )}
                        <Button
                            rightIcon="arrow_forward"
                            label={`Next`}
                            variation="primary"
                            disabled={!canProceed}
                            onClick={handleNextClicked}
                        />
                    </div>
                </>
            )}
        </div >
    );
}